import { createContext, useContext, useState, useEffect } from 'react';
import { getAdminMe } from '../../services/capabilityService';
import { runAllSmokeChecks } from '../../utils/adminAuthSmokeChecks';

const CapabilityContext = createContext(null);

/**
 * Capability Provider
 * 
 * Loads admin user capabilities once and provides them via context.
 * 
 * Provides:
 * - loading: boolean (true while fetching)
 * - error: Error | null (error if fetch failed)
 * - me: object | null (user info with capabilities)
 * - has(capability): function (check if user has a capability)
 */
export function CapabilityProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Expose refresh function via context
  const refreshCapabilities = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminMe(true); // Force refresh
      setMe(data);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  useEffect(() => {
    let cancelled = false;
    let refreshAttempted = false; // Prevent infinite refresh loops

    async function loadCapabilities() {
      try {
        setLoading(true);
        setError(null);
        const data = await getAdminMe();
        
        if (!cancelled) {
          setMe(data);
          setLoading(false);
          
          // Run smoke checks
          runAllSmokeChecks({ me: data, has: (cap) => data?.capabilities?.includes(cap) || false });
          
          // GUARDRAIL: Auto-detect capability mismatch
          // If user is super admin but has no capabilities, auto-refresh once
          if (data?.isSuperAdmin && (!data?.capabilities || data.capabilities.length === 0) && !refreshAttempted) {
            console.warn('[RBAC] Capability mismatch detected: Super admin has no capabilities. Auto-refreshing...');
            refreshAttempted = true;
            // Auto-refresh after a short delay to avoid infinite loops
            setTimeout(async () => {
              if (!cancelled) {
                try {
                  const refreshed = await getAdminMe(true);
                  if (!cancelled) {
                    if (refreshed?.capabilities && refreshed.capabilities.length > 0) {
                      setMe(refreshed);
                      console.info('[RBAC] Auto-refresh successful: Capabilities restored');
                    } else {
                      console.error('[RBAC] Auto-refresh failed: Still no capabilities after refresh');
                      // Set error state so UI can show recovery options
                      setError(new Error('Capabilities not available. Please refresh permissions.'));
                    }
                  }
                } catch (err) {
                  console.error('[RBAC] Auto-refresh failed:', err);
                  setError(err);
                }
              }
            }, 1000);
          }
        }
      } catch (err) {
        if (!cancelled) {
          // Check if error is 401/unauthenticated
          const isUnauth = err?.isUnauthenticated || err?.status === 401 || err?.code === 'UNAUTHENTICATED' || 
                          err?.message?.includes('UNAUTHENTICATED') || err?.message?.includes('Authentication required');
          
          setError(err);
          setLoading(false);
          
          // If unauthenticated, redirect to login (but only if not already on login page)
          if (isUnauth && typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin/login')) {
            console.log('[CapabilityProvider] Unauthenticated, redirecting to login');
            window.location.href = '/admin/login';
          }
        }
      }
    }

    loadCapabilities();

    return () => {
      cancelled = true;
    };
  }, [refreshTrigger]);

  /**
   * Check if user has a specific capability
   * @param {string} capability - Capability to check (e.g., 'admin:integrations:manage')
   * @returns {boolean} True if user has the capability
   */
  const has = (capability) => {
    if (!me || !me.capabilities) {
      return false;
    }
    return me.capabilities.includes(capability);
  };

  // Determine auth state
  const isUnauthenticated = error?.isUnauthenticated || error?.status === 401 || error?.code === 'UNAUTHENTICATED';
  const isUnauthorized = error?.isUnauthorized || error?.status === 403 || error?.code === 'UNAUTHORIZED';
  const isAuthenticated = !loading && !error && me !== null;
  const isSignedOut = isUnauthenticated || (error && !isUnauthorized);

  // Expose isSuperAdmin directly for easy access
  const isSuperAdmin = me?.isSuperAdmin === true;

  const value = {
    loading,
    error,
    me,
    has,
    refreshCapabilities,
    isUnauthenticated,
    isUnauthorized,
    isAuthenticated,
    isSignedOut,
    isSuperAdmin, // Direct access to super admin status
  };

  return (
    <CapabilityContext.Provider value={value}>
      {children}
    </CapabilityContext.Provider>
  );
}

/**
 * Hook to use capability context
 * @returns {object} { loading, error, me, has }
 */
export function useCapabilities() {
  const context = useContext(CapabilityContext);
  if (!context) {
    throw new Error('useCapabilities must be used within CapabilityProvider');
  }
  return context;
}

